import API from "../../services/api";
import { asArray } from "./rosterSafeArrays";

function wfErr(e, fallback) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || String(d)).join("; ");
  return e?.message || fallback;
}

export async function fetchWfLabels(locale = "en") {
  const res = await API.get("/wf/labels", { params: { locale } });
  return res.data;
}

export async function fetchWfAttendanceProfile() {
  const res = await API.get("/wf/attendance-profile");
  return res.data;
}

export async function fetchWfSetupStatus() {
  const { loadWfSetupStatus } = await import("../../services/appQueries");
  return loadWfSetupStatus();
}

export async function fetchWfSetupOptions() {
  const res = await API.get("/wf/setup/options");
  return res.data;
}

export async function saveWfSetupProgress(step, payload) {
  const res = await API.put("/wf/setup/progress", { step, payload });
  return res.data;
}

export async function completeWfSetup(payload) {
  const res = await API.post("/wf/setup/complete", payload);
  return res.data;
}

export async function updateAttendanceConfiguration(source) {
  const res = await API.patch("/wf/setup/configuration", { source });
  return res.data;
}

export async function updateWorkScheduleConfiguration(payload) {
  const res = await API.patch("/wf/setup/work-schedule", payload);
  return res.data;
}

export async function completeWfSetupFromProgress() {
  const res = await API.post("/wf/setup/complete-from-progress");
  return res.data;
}

export async function fetchWfOnboarding() {
  return fetchWfSetupStatus();
}

/** Re-fetch setup + task validation state (uses GET /setup/status — no separate refresh POST). */
export async function refreshWfSetupTasks() {
  return fetchWfSetupStatus();
}

export async function validateWfSetupTask(taskCode) {
  const res = await API.post(`/wf/setup/tasks/${taskCode}/validate`);
  return res.data;
}

export async function completeWfSetupTask(taskCode) {
  const res = await API.post(`/wf/setup/tasks/${taskCode}/complete`);
  return res.data;
}

export async function fetchWfShiftTemplates(params = {}) {
  const res = await API.get("/wf/shift-templates", { params: { page_size: 200, ...params } });
  const data = res.data;
  return data.items ?? data;
}

export async function createWfShiftTemplate(data) {
  const res = await API.post("/wf/shift-templates", data);
  return res.data;
}

export async function patchWfSourceConfig(sourceCode, data) {
  const res = await API.patch(`/wf/sources/config/${sourceCode}`, data);
  return res.data;
}

export async function activateWfAttendanceProfile(payload) {
  const res = await API.post("/wf/attendance-profile/activate", payload);
  return res.data;
}

export async function fetchWfEnabledModes() {
  const res = await API.get("/wf/attendance-profile/modes");
  return res.data;
}

export async function fetchWfSourcePlugins() {
  const res = await API.get("/wf/sources");
  return res.data;
}

export async function fetchWfHolidays(params = {}) {
  const res = await API.get("/wf/holidays", { params });
  return asArray(res.data);
}

export async function createWfHoliday(data) {
  const res = await API.post("/wf/holidays", data);
  return res.data;
}

export async function updateWfHoliday(holidayId, data) {
  const res = await API.put(`/wf/holidays/${holidayId}`, data);
  return res.data;
}

export async function deleteWfHoliday(holidayId) {
  await API.delete(`/wf/holidays/${holidayId}`);
}

export async function downloadHolidayTemplate() {
  const res = await API.get("/wf/holidays/upload/template", { responseType: "blob" });
  return res.data;
}

export async function previewHolidayUpload(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await API.post("/wf/holidays/upload/preview", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function importHolidayUpload(file) {
  const form = new FormData();
  form.append("file", file);
  const res = await API.post("/wf/holidays/upload/import", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function fetchWfFeatureFlags() {
  const res = await API.get("/wf/feature-flags");
  return res.data;
}

export async function patchWfFeatureFlag(flagCode, data) {
  const res = await API.patch(`/wf/feature-flags/${flagCode}`, data);
  return res.data;
}

export async function ingestWfRawEvent(data) {
  const res = await API.post("/wf/events/ingest", data);
  return res.data;
}

export async function startWfRecompute(data) {
  const res = await API.post("/wf/jobs/recompute", data);
  return res.data;
}

/** Synchronous attendance engine recompute (employee-day or range). */
export async function recomputeWfAttendance(data) {
  const res = await API.post("/wf/attendance/recompute", data);
  return res.data;
}

export async function fetchWfExceptions(params = {}) {
  const { statusFilter, fromDate, toDate, exceptionType } = params;
  const query = {};
  if (statusFilter) query.status_filter = statusFilter;
  if (fromDate) query.from_date = fromDate;
  if (toDate) query.to_date = toDate;
  if (exceptionType) query.exception_type = exceptionType;
  const res = await API.get("/wf/exceptions", { params: query });
  return res.data;
}

export async function seedWfPlatform() {
  const res = await API.post("/wf/platform/seed");
  return res.data;
}

export async function fetchWfPolicies() {
  const res = await API.get("/wf/policies");
  return res.data;
}

export async function createWfPolicy(data) {
  const res = await API.post("/wf/policies", data);
  return res.data;
}

export async function publishWfPolicy(policyId) {
  const res = await API.post(`/wf/policies/${policyId}/publish`);
  return res.data;
}

export async function addWfPolicyRule(policyId, data) {
  const res = await API.post(`/wf/policies/${policyId}/rules`, data);
  return res.data;
}

export async function fetchWfApprovals(params = {}) {
  const { statusFilter = "pending", entityType } = params;
  try {
    const query = {};
    if (statusFilter) query.status_filter = statusFilter;
    if (entityType) query.entity_type = entityType;
    const res = await API.get("/wf/approvals", { params: query });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    throw new Error(wfErr(e, "Failed to load attendance approvals"));
  }
}

export async function submitOtRequest({ employee_id, work_date, hours, reason }) {
  try {
    const payload = { work_date, hours, reason: reason || undefined };
    const res = await API.post("/wf/approvals", {
      workflow_type: "attendance",
      entity_type: "ot_request",
      entity_id: crypto.randomUUID(),
      employee_id,
      payload_json: payload,
    });
    return res.data;
  } catch (e) {
    throw new Error(wfErr(e, "Failed to submit OT request"));
  }
}

export async function decideWfApproval(requestId, data) {
  try {
    const res = await API.post(`/wf/approvals/${requestId}/decide`, data);
    return res.data;
  } catch (e) {
    throw new Error(wfErr(e, "Failed to update approval"));
  }
}

export async function patchWfLabels(data) {
  const res = await API.patch("/wf/labels", data);
  return res.data;
}

export async function fetchWfRosters() {
  const res = await API.get("/wf/rosters");
  return res.data;
}

export async function createWfRoster(data) {
  const res = await API.post("/wf/rosters", data);
  return res.data;
}

export async function publishWfRoster(rosterPlanId) {
  const res = await API.post(`/wf/rosters/${rosterPlanId}/publish`);
  return res.data;
}

export async function addWfRosterAssignments(rosterPlanId, assignments, adminOverride = false) {
  const res = await API.post(`/wf/rosters/${rosterPlanId}/assignments`, {
    assignments,
    admin_override: adminOverride,
  });
  return res.data;
}

export {
  searchEmployees,
  fetchWfShifts,
  fetchRosterAssignmentsGrid,
  updateRosterAssignment,
  deleteRosterAssignment,
  bulkCopyRosterAssignments,
  fetchRosterAssignmentAudit,
  fetchRosterStateLog,
  adminReopenRoster,
  updateWfRoster,
  deleteWfRoster,
} from "./rosterApi";

export async function fetchWfAnalytics(fromDate, toDate) {
  const res = await API.get("/wf/analytics/dashboard", {
    params: { from_date: fromDate, to_date: toDate },
  });
  return res.data;
}

export async function fetchWfOpsMetrics() {
  const res = await API.get("/wf/enterprise/ops/metrics");
  return res.data;
}

export async function fetchWfPolicyExecutionLogs(params = {}) {
  const res = await API.get("/wf/enterprise/policy-execution-logs", { params });
  return res.data;
}

export async function refreshWfProjections(fromDate, toDate) {
  const res = await API.post("/wf/enterprise/projections/refresh", null, {
    params: { from_date: fromDate, to_date: toDate },
  });
  return res.data;
}

export async function submitWfRoster(rosterPlanId, notes) {
  const res = await API.post(`/wf/enterprise/rosters/${rosterPlanId}/submit`, { notes });
  return res.data;
}

export async function approveWfRoster(rosterPlanId) {
  const res = await API.post(`/wf/enterprise/rosters/${rosterPlanId}/approve`);
  return res.data;
}

export async function freezeWfRoster(rosterPlanId) {
  const res = await API.post(`/wf/enterprise/rosters/${rosterPlanId}/freeze`);
  return res.data;
}

export async function archiveWfRoster(rosterPlanId) {
  const res = await API.post(`/wf/enterprise/rosters/${rosterPlanId}/archive`);
  return res.data;
}

export async function applyWfFreeze(data) {
  const res = await API.post("/wf/enterprise/freeze", data);
  return res.data;
}

export async function fetchWfDevices() {
  const res = await API.get("/wf/enterprise/devices");
  return res.data;
}

export async function registerWfDevice(data) {
  const res = await API.post("/wf/enterprise/devices", data);
  return res.data;
}

export async function fetchWfTerminals() {
  const res = await API.get("/wf/enterprise/terminals");
  return res.data;
}

export async function fetchWfPolicyLogDetail(logId) {
  const res = await API.get(`/wf/enterprise/policy-execution-logs/${logId}`);
  return res.data;
}

export async function fetchWfWorkerHealth() {
  const res = await API.get("/wf/enterprise/ops/worker-health");
  return res.data;
}

export async function fetchWfQueueMetrics() {
  const res = await API.get("/wf/enterprise/ops/queue-metrics");
  return res.data;
}

export async function fetchWfProjectionSummary(fromDate, toDate) {
  const res = await API.get("/wf/enterprise/projections/summary", {
    params: { from_date: fromDate, to_date: toDate },
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Freeze management (attendance / payroll / financial)
// ---------------------------------------------------------------------------

export async function createWfFreeze(data) {
  const res = await API.post("/wf/freeze", data);
  return res.data;
}

export async function releaseWfFreeze(freezeId) {
  await API.delete(`/wf/freeze/${freezeId}`);
}

export async function fetchWfFreezes(params = {}) {
  const res = await API.get("/wf/freeze", { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Biometric punch ingestion
// ---------------------------------------------------------------------------

export async function ingestBiometricPunch(data) {
  const res = await API.post("/wf/biometric/punch", data);
  return res.data;
}

export async function ingestBiometricBatch({ terminal_code, punches }) {
  const res = await API.post("/wf/biometric/batch", { terminal_code, punches });
  return res.data;
}

// ---------------------------------------------------------------------------
// Employee-specific shift assignment
// ---------------------------------------------------------------------------

export async function assignEmployeeShift(data) {
  // data: { employee_id, shift_id?, template_id?, effective_from, effective_to?, reason? }
  const res = await API.post("/wf/employee-shifts", data);
  return res.data;
}

export async function fetchEmployeeShifts(employeeId, params = {}) {
  const res = await API.get(`/wf/employee-shifts/${employeeId}`, { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Attendance override & recompute protection
// ---------------------------------------------------------------------------

export async function applyAttendanceOverride(attendanceId, data) {
  // data: { override_status?, override_fraction?, waive_late?, approve_overtime?,
  //         reason (required), protect_from_recompute? }
  const res = await API.post(`/wf/attendance/${attendanceId}/override`, data);
  return res.data;
}

export async function clearAttendanceRecomputeProtection(attendanceId, reason) {
  const res = await API.post(`/wf/attendance/${attendanceId}/clear-protection`, { reason });
  return res.data;
}

export async function lockAttendanceRow(attendanceId) {
  const res = await API.post(`/attendance/${attendanceId}/lock`);
  return res.data;
}

export async function unlockAttendanceRow(attendanceId) {
  const res = await API.post(`/attendance/${attendanceId}/unlock`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Attendance correction requests (ESS / HR)
// ---------------------------------------------------------------------------

export async function submitAttendanceCorrectionRequest({
  employee_id,
  work_date,
  correction_type,
  requested_values,
  reason,
}) {
  // Backend: simple type params are query params; dict param is JSON body
  const res = await API.post(
    "/attendance/correction-requests",
    requested_values,
    { params: { employee_id, work_date, correction_type, reason: reason || undefined } },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Calendar day preview (non-destructive resolution)
// ---------------------------------------------------------------------------

export async function previewCalendarDay(employeeId, workDate) {
  const res = await API.get("/attendance/preview/calendar-day", {
    params: { employee_id: employeeId, work_date: workDate },
  });
  return res.data;
}

export async function previewWfCalendar(employeeId, workDate) {
  const res = await API.get("/wf/calendar/preview", {
    params: { employee_id: employeeId, work_date: workDate },
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Live attendance (daily view with WF engine results)
// ---------------------------------------------------------------------------

export async function fetchWfDailyAttendance(params = {}) {
  // params: { from_date, to_date, employee_id }
  const res = await API.get("/attendance/", { params });
  return Array.isArray(res.data) ? res.data : res.data?.items ?? [];
}

export async function fetchWfRecomputeJobStatus(jobId) {
  const res = await API.get(`/wf/jobs/${jobId}`);
  return res.data;
}

// ---------------------------------------------------------------------------
// Enterprise Biometric layer (/wf/biometric-enterprise)
// ---------------------------------------------------------------------------

const BIO = "/wf/biometric-enterprise";

export async function fetchBiometricProviders() {
  const res = await API.get(`${BIO}/providers`);
  return res.data?.providers ?? [];
}

export async function fetchDirectAdmsServerConfig() {
  const res = await API.get(`${BIO}/direct-adms/server-config`);
  return res.data;
}

export async function fetchBiometricDashboard() {
  const res = await API.get(`${BIO}/dashboard`);
  return res.data;
}

export async function fetchBiometricDevices(params = {}) {
  const res = await API.get(`${BIO}/devices`, { params });
  return Array.isArray(res.data) ? res.data : [];
}

export async function createBiometricDevice(data) {
  const res = await API.post(`${BIO}/devices`, data);
  return res.data;
}

export async function updateBiometricDevice(deviceId, data) {
  const res = await API.patch(`${BIO}/devices/${deviceId}`, data);
  return res.data;
}

export async function syncBiometricDevice(deviceId, payload = {}) {
  const res = await API.post(`${BIO}/devices/${deviceId}/sync`, payload);
  return res.data;
}

export async function healthCheckBiometricDevice(deviceId) {
  const res = await API.post(`${BIO}/devices/${deviceId}/health-check`);
  return res.data;
}

export async function fetchBiometricSyncLogs(deviceId, limit = 50) {
  const res = await API.get(`${BIO}/devices/${deviceId}/sync-logs`, { params: { limit } });
  return Array.isArray(res.data) ? res.data : [];
}

export async function fetchBiometricMappings(params = {}) {
  const res = await API.get(`${BIO}/mappings`, { params });
  return Array.isArray(res.data) ? res.data : [];
}

export async function createBiometricMapping(data) {
  const res = await API.post(`${BIO}/mappings`, data);
  return res.data;
}

export async function updateBiometricMapping(mapId, data) {
  const res = await API.patch(`${BIO}/mappings/${mapId}`, data);
  return res.data;
}

export async function deleteBiometricMapping(mapId, hard = false) {
  const res = await API.delete(`${BIO}/mappings/${mapId}`, { params: { hard } });
  return res.data;
}

export async function fetchBiometricAudit(params = {}) {
  const res = await API.get(`${BIO}/audit`, { params });
  return Array.isArray(res.data) ? res.data : [];
}
